import { pdf, PDFViewer } from "@react-pdf/renderer";
import { useCallback, useState } from "react";
import CreditAuthorizationPDF from "../components/pdf/CreditAuthorizationPDF";
import LoanApplicationPDF from "../components/pdf/LoanApplicationPDF";
import FundingShieldPDF from "../components/pdf/FundingShieldPDF/FundingShieldPDF";
import LoanTermsSheetPDF from "../components/pdf/LoanTermsSheetPDF/LoanTermsSheetPDF";
import LoanTermsSheetHhbPDF from "../components/pdf/LoanTermsSheetPDF/LoanTermsSheetHhbPDF";

export const usePdfDownloadHook = ({
  formData,
  filename = "document.pdf",
  type,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  // Get the PDF component based on type
  const getPDFComponent = useCallback(
    ({ formData }) => {
      if (type === "credit-authorization") {
        return <CreditAuthorizationPDF formData={formData} />;
      }
      if (type === "loan-application") {
        return <LoanApplicationPDF formData={formData} />;
      }
      if (type === "loan-application") {
        return <LoanApplicationPDF formData={formData} />;
      }
      if (type === "funding-shield") {
        return <FundingShieldPDF formData={formData} />;
      }
      if (type === "loan-terms-sheet") {
        return <LoanTermsSheetPDF formData={formData} />;
      }
      if (type === "loan-terms-sheet-hhb") {
        return <LoanTermsSheetHhbPDF formData={formData} />;
      }
      return null;
    },
    [type],
  );

  // Download PDF functionality
  const downloadPDF = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const pdfComponent = getPDFComponent({ formData });
      if (!pdfComponent) {
        throw new Error("Invalid PDF type");
      }

      const blob = await pdf(pdfComponent).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err);
      console.error("Error generating PDF:", err);
    } finally {
      setLoading(false);
    }
  }, [formData, filename, getPDFComponent]);

  // Toggle preview mode
  const togglePreview = useCallback(() => {
    setPreviewMode((prev) => !prev);
  }, []);

  // Open preview in new window
  const openPreviewInNewWindow = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const pdfComponent = getPDFComponent({ formData });
      if (!pdfComponent) {
        throw new Error("Invalid PDF type");
      }

      const blob = await pdf(pdfComponent).toBlob();
      const url = URL.createObjectURL(blob);

      const newWindow = window.open(url, "_blank");
      if (!newWindow) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }

      // Clean up URL after a delay to ensure it loads
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      setError(err);
      console.error("Error opening PDF preview:", err);
    } finally {
      setLoading(false);
    }
  }, [getPDFComponent]);

  // PDF Preview Component
  const PDFPreviewComponent = useCallback(() => {
    const pdfComponent = getPDFComponent({ formData });
    if (!pdfComponent) return null;

    return (
      <PDFViewer style={{ width: "100%", height: "100%" }}>
        {pdfComponent}
      </PDFViewer>
    );
  }, [getPDFComponent, formData]);

  return {
    downloadPDF,
    loading,
    error,
    previewMode,
    togglePreview,
    openPreviewInNewWindow,
    PDFPreviewComponent,
  };
};
